import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PATH_LIST } from "shared/lib/react-router";
import { EntityType } from "static/types";
import { KYC_Status, KYC_Step, KYB_Step, accountStore } from "entities/account";
import { useStore } from "zustand";
import { sessionStore } from "entities/session/model/sessionModel";

enum Status {
  Start = "start",
  // Residence = "residence",
  // Name = "name",
  CompanyName = "company-name",
  // DoB = "dob",
  // Address = "address",
  Phone = "phone",
  Email = "email",
  ArmDocument = "arm-document",
  Credentials = "credentials",
  // Document = "document",
  // Selfie = "selfie",
  // PoA = "poa",
  Documents = "documents",
  PinCode = "pin",
  CRSData = "crs-data",
  Contract = "contract",
}

const PathByStatus: Record<Status, string> = {
  [Status.Start]: PATH_LIST.verification,
  // [Status.Residence]: PATH_LIST.residence,
  // [Status.Name]: PATH_LIST.details,
  [Status.CompanyName]: PATH_LIST.details,
  // [Status.DoB]: PATH_LIST.dateOfBirth,
  // [Status.Address]: PATH_LIST.residenceAddress,
  [Status.Phone]: PATH_LIST.phoneNumber,
  [Status.Email]: PATH_LIST.email,
  [Status.ArmDocument]: PATH_LIST.identityVerification,
  [Status.Credentials]: PATH_LIST.uploadCredentials,
  // [Status.Document]: PATH_LIST.chooseDocType,
  // [Status.Selfie]: PATH_LIST.selfieWithDocument,
  // [Status.PoA]: PATH_LIST.proofOfAddress,
  [Status.Documents]: PATH_LIST.businessDocuments,
  [Status.PinCode]: PATH_LIST.pinCode,
  [Status.CRSData]: PATH_LIST.taxInformation,
  [Status.Contract]: PATH_LIST.signUserContract,
};

export function useVerificationNavigation() {
  const navigate = useNavigate();
  const { entity, user, crsData } = useStore(sessionStore);
  const { kyc, kyb } = useStore(accountStore);
  const location = useLocation();
  const [verificationRequestModal, setVerificationRequestModal] = useState(
    location.state?.verificationRequestModal || false
  );

  const [status, setStatus] = useState<Status>();

  useEffect(() => {
    if (entity === EntityType.Individual) {
      if (!kyc) {
        setStatus(Status.Start);
        return;
      }

      if (kyc.status === KYC_Status.Success) {
        if (!user?.hasPinCode) {
          setStatus(Status.PinCode);
        } else if (!crsData || crsData.length === 0) {
          setStatus(Status.CRSData);
        } else if (!user?.signContract) {
          setStatus(Status.Contract);
        } else {
          setStatus(undefined);
        }
      } else if (kyc.status === KYC_Status.Pending) {
        setStatus(undefined);
      } else {
        if (kyc.step === KYC_Step.EmailVerification) {
          setStatus(Status.Email);
        } else if (kyc.step === KYC_Step.PhoneVerification) {
          setStatus(Status.Phone);
        } else if (kyc.step === KYC_Step.Documents) {
          setStatus(Status.Credentials);
        } else {
          setStatus(Status.Start);
        }
      }
    } else {
      if (!kyb) {
        setStatus(Status.Start);
        return;
      }

      if (kyb.status === KYC_Status.Success) {
        if (!user?.hasPinCode) {
          setStatus(Status.PinCode);
        } else if (!user?.signContract) {
          setStatus(Status.Contract);
        } else {
          setStatus(undefined);
        }
      } else if (kyb.status === KYC_Status.Pending) {
        setStatus(undefined);
      } else {
        if (kyb.step === KYB_Step.None) {
          setStatus(Status.Start);
        } else if (kyb.step === KYB_Step.Name) {
          setStatus(Status.CompanyName);
        } else if (kyb.step === KYB_Step.EmailVerification) {
          setStatus(Status.Email);
        } else if (kyb.step === KYB_Step.PhoneVerification) {
          setStatus(Status.Phone);
        } else if (kyb.step === KYB_Step.Documents) {
          setStatus(Status.Documents);
        }
      }
    }
  }, [entity, user, kyc, kyb, crsData]);

  const navigateVerification = useCallback(() => {
    if (status) navigate(PathByStatus[status]);
    else {
      if (entity === EntityType.Individual) {
        if (kyc) {
          if (kyc.status === KYC_Status.Pending) {
            setVerificationRequestModal(true);
          } else {
            setStatus(undefined);
          }
        } else {
          navigate(PATH_LIST.verification);
        }
      } else {
        if (kyb) {
          if (kyb.status === KYC_Status.Pending) {
            setVerificationRequestModal(true);
          } else {
            setStatus(undefined);
          }
        } else {
          navigate(PATH_LIST.verification);
        }
      }
    }
  }, [entity, kyc, kyb, status, navigate]);

  return {
    navigateVerification,
    verificationRequestModal,
    setVerificationRequestModal,
    status,
  };
}
